import React, { useState, useEffect } from 'react';
import {
  Container, TextField, Button, Typography, Link, Box,
  Alert, InputAdornment, IconButton, CircularProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SignupModal from '../modals/SignupModal';
import ForgotPasswordModal from '../modals/ForgotPasswordModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../apiFetch';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LockIcon from '@mui/icons-material/Lock';
import {
  isPlatformAuthenticatorAvailable,
  parseAuthenticationOptions,
  prepareCredentialForServer,
} from '../utils/webauthn';

// Icons
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const Login: React.FC = () => {
  // Pre-fill with last logged-in username for faster biometric login
  const [identifier, setIdentifier] = useState<string>(() => {
    return localStorage.getItem('last_username') || '';
  });
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isBiometricSubmitting, setIsBiometricSubmitting] = useState<boolean>(false);

  // Biometric login state
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [showBiometricOption, setShowBiometricOption] = useState(false);

  // 2FA state
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [tempToken, setTempToken] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState<string>('');
  const [is2FASubmitting, setIs2FASubmitting] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string>('');

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const message = params.get('message');
  const apiUrl = process.env.REACT_APP_API_URL;

  // Check WebAuthn support
  useEffect(() => {
    const checkSupport = async () => {
      const supported = await isPlatformAuthenticatorAvailable();
      setBiometricSupported(supported);
    };
    checkSupport();
  }, []);

  // Show biometric option when username is entered
  useEffect(() => {
    setShowBiometricOption(identifier.length > 0 && biometricSupported);
  }, [identifier, biometricSupported]);

  // Handle password login
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const data = await apiFetch(`${apiUrl}/api/token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: identifier, password }),
      });

      // Check if 2FA is required
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setShow2FADialog(true);
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
      localStorage.setItem('last_username', identifier);
      // Set flag for PWA prompt if user just confirmed their email
      if (message === 'email_confirmed') {
        localStorage.setItem('show_pwa_prompt', 'true');
      }
      window.location.href = '/';
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle 2FA verification
  const handle2FAVerify = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setTwoFactorError('Please enter a 6-digit code');
      return;
    }

    setIs2FASubmitting(true);
    setTwoFactorError('');

    try {
      const data = await apiFetch(`${apiUrl}/api/2fa/verify/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          temp_token: tempToken,
          code: twoFactorCode,
          purpose: 'login',
        }),
      });

      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
      localStorage.setItem('last_username', identifier);
      // Set flag for PWA prompt if user just confirmed their email
      if (message === 'email_confirmed') {
        localStorage.setItem('show_pwa_prompt', 'true');
      }
      window.location.href = '/';
    } catch (err: unknown) {
      const error = err as Error;
      setTwoFactorError(error.message || '2FA verification failed');
    } finally {
      setIs2FASubmitting(false);
    }
  };

  // Close 2FA dialog
  const close2FADialog = () => {
    setShow2FADialog(false);
    setTempToken('');
    setTwoFactorCode('');
    setTwoFactorError('');
  };

  // Handle biometric login
  const handleBiometricLogin = async () => {
    if (!identifier) {
      setError('Please enter your username or email first');
      return;
    }

    setIsBiometricSubmitting(true);
    setError('');

    try {
      // Step 1: Get authentication options from server
      const optionsResponse = await apiFetch(`${apiUrl}/api/webauthn/authenticate/options/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: identifier }),
      });

      // Step 2: Authenticate with browser API
      const publicKeyOptions = parseAuthenticationOptions(optionsResponse);
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Authentication cancelled');
      }

      // Step 3: Verify with server and get tokens
      const credentialForServer = prepareCredentialForServer(credential);
      const data = await apiFetch(`${apiUrl}/api/webauthn/authenticate/verify/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentialForServer),
      });

      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
      localStorage.setItem('last_username', identifier);
      window.location.href = '/';

    } catch (err: unknown) {
      const error = err as Error & { name?: string };
      if (error.name === 'NotAllowedError') {
        setError('Biometric authentication was cancelled.');
      } else if (error.message?.includes('No biometric credentials')) {
        setError('No biometric login set up for this account. Please use your password.');
      } else {
        setError(error.message || 'Biometric login failed');
      }
    } finally {
      setIsBiometricSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Login</Typography>
        </Box>
      </Box>
      {message === 'token_expired' && (<Alert severity="warning" sx={{ mb: 2 }}>Your session has expired. Please log in again.</Alert>)}
      {message === 'email_confirmed' && (<Alert severity="success" sx={{ mb: 2 }}>Your email has been confirmed! Please log in to continue.</Alert>)}
      {message === 'account_deactivated' && (<Alert severity="success" sx={{ mb: 2 }}>Your account has been deactivated. Your data has been preserved.</Alert>)}
      {message === 'account_deleted' && (<Alert severity="success" sx={{ mb: 2 }}>Your account and all data have been permanently deleted.</Alert>)}
      {error && (<Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>)}

      <form onSubmit={handleLogin}>
        <TextField
          label="Username or Email"
          variant="outlined"
          fullWidth
          margin="normal"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
        />
        <TextField
          label="Password"
          variant="outlined"
          fullWidth
          margin="normal"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            autoCapitalize: 'none',
            autoCorrect: 'off',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          sx={{ mt: 2 }}
          disabled={isSubmitting || isBiometricSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Logging in...' : 'Login'}
        </Button>
      </form>

      {/* Biometric Login Option */}
      {showBiometricOption && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">OR</Typography>
          </Divider>
          <Button
            variant="outlined"
            fullWidth
            startIcon={isBiometricSubmitting ? <CircularProgress size={20} /> : <FingerprintIcon />}
            onClick={handleBiometricLogin}
            disabled={isSubmitting || isBiometricSubmitting}
          >
            {isBiometricSubmitting ? 'Authenticating...' : 'Login with Biometrics'}
          </Button>
        </>
      )}

      {/* Links for forgot password and signup */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => setForgotModalOpen(true)}
        >
          Forgot Password?
        </Link>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => setSignupModalOpen(true)}
        >
          Sign Up
        </Link>
      </Box>

      {/* Modals for signup and forgot password */}
      <SignupModal open={signupModalOpen} onClose={() => setSignupModalOpen(false)} />
      <ForgotPasswordModal open={forgotModalOpen} onClose={() => setForgotModalOpen(false)} />

      {/* 2FA Verification Dialog */}
      <Dialog open={show2FADialog} onClose={close2FADialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="primary" />
          Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 6-digit code from your authenticator app.
          </Typography>
          {twoFactorError && (
            <Alert severity="error" sx={{ mb: 2 }}>{twoFactorError}</Alert>
          )}
          <TextField
            label="Authentication Code"
            variant="outlined"
            fullWidth
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' }
            }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && twoFactorCode.length === 6) {
                handle2FAVerify();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close2FADialog} disabled={is2FASubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handle2FAVerify}
            disabled={is2FASubmitting || twoFactorCode.length !== 6}
            startIcon={is2FASubmitting ? <CircularProgress size={20} /> : null}
          >
            {is2FASubmitting ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;